/**
 * Feedback API
 * Issue #1075: In-app feedback modal
 */

import { apiPost, apiGet, apiPatch } from '../api';

export interface FeedbackResponses {
  [questionKey: string]: string | string[];
}

export interface SubmitFeedbackPayload {
  responses: FeedbackResponses;
  club_id?: string;
  app_version?: string; // e.g., "1.2.1-dev #1234"
}

export interface SubmitFeedbackResponse {
  feedback: { id: string };
  message: string;
}

export interface FeedbackItem {
  id: string;
  user_id: string;
  club_id: string | null;
  subscription_tier: string;
  responses: FeedbackResponses;
  received_at: number;
  closed_at: number | null;
  status: 'new' | 'reviewed' | 'closed';
  user_agent: string;
  app_version: string | null;
  first_name: string;
  last_name: string;
  email: string;
  club_name: string | null;
  // GitHub issue linkage (#1300)
  github_issue_number: number | null;
  github_issue_url: string | null;
  github_issue_created_at: number | null;
}

export interface CreateGitHubIssueResponse {
  url: string;
  number: number;
  created: boolean;
}

export interface FeedbackListResponse {
  feedback: FeedbackItem[];
  count: number;
  limit: number;
  offset: number;
}

export const feedbackApi = {
  /**
   * Submit feedback (authenticated user)
   * POST /feedback
   */
  submit: (payload: SubmitFeedbackPayload) =>
    apiPost<SubmitFeedbackResponse>('/feedback', payload),

  /**
   * List all feedback (platform admin only)
   * GET /admin/feedback
   */
  list: (params?: { status?: string; limit?: number; offset?: number }) => {
    const query = new URLSearchParams();
    if (params?.status) query.append('status', params.status);
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    return apiGet<FeedbackListResponse>(`/admin/feedback?${query.toString()}`);
  },

  /**
   * Update feedback status (platform admin only)
   * PATCH /admin/feedback/:id
   */
  updateStatus: (id: string, status: 'new' | 'reviewed' | 'closed') =>
    apiPatch<{ message: string; status: string }>(`/admin/feedback/${id}`, { status }),

  /**
   * Create GitHub issue from feedback (platform admin only)
   * POST /admin/feedback/:id/github-issue
   * Issue #1300
   */
  createGitHubIssue: (id: string) =>
    apiPost<CreateGitHubIssueResponse>(`/admin/feedback/${id}/github-issue`, {}),
};
